import type { RealtimeChannel } from '@supabase/supabase-js';
import type { BsyncClient } from './client';

export type RealtimeState = 'connecting' | 'live' | 'down';

export interface RealtimeHandlers {
  /** The user's revision moved: something changed on the server. */
  onSyncChange: () => void;
  onDevicesChange?: () => void;
  onExtensionsChange?: () => void;
  onState?: (s: RealtimeState) => void;
}

/**
 * Change notifications only — payloads are ignored and every notification
 * triggers an ordinary cursor-based pull. A dropped socket therefore loses
 * nothing: on resubscribe we pull again, and periodic syncs cover the rest.
 */
export function subscribeToChanges(client: BsyncClient, userId: string, h: RealtimeHandlers): () => void {
  const filter = `user_id=eq.${userId}`;
  let channel: RealtimeChannel = client
    .channel(`bsync:${userId}:${Math.random().toString(36).slice(2, 8)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'user_sync', filter }, () => h.onSyncChange());
  if (h.onDevicesChange) {
    channel = channel.on('postgres_changes', { event: '*', schema: 'public', table: 'devices', filter }, () => h.onDevicesChange!());
  }
  if (h.onExtensionsChange) {
    channel = channel.on('postgres_changes', { event: '*', schema: 'public', table: 'device_extensions', filter }, () => h.onExtensionsChange!());
  }
  h.onState?.('connecting');
  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      h.onState?.('live');
      h.onSyncChange(); // catch up on anything missed while disconnected
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      h.onState?.('down');
    }
  });
  return () => {
    void client.removeChannel(channel);
  };
}
